import { useState, useMemo, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Save, Loader2, ShoppingBag, Package } from "lucide-react";
import AddProductDialog from "@/components/AddProductDialog";
import { adminCreateOrder, createInvoice, updateInvoice, getProfile } from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

/**
 * DraftOrderSheet — admin builds a new order client-side; no Airtable record
 * exists until the admin clicks Save. Cancel discards everything.
 */
export default function DraftOrderSheet({
  open,
  onOpenChange,
  customer, // { id, full_name, email, phone, address, address2, city, state, postcode, country }
  callerUserRecordId,
  dealerMode = false, // approved dealer (not admin) → order flagged for review
  editInvoice = null, // when set, the sheet edits this existing invoice
  onCreated, // (response) => void   (parent reloads the CRM)
}) {
  const [items, setItems] = useState([]);
  const [shipping, setShipping] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [discount, setDiscount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  // Dealers can save either an invoice (their own ledger) or a real order
  // that goes to the OreVital team for review — same as the admin "New order".
  const [docKind, setDocKind] = useState("invoice");
  const asOrder = !dealerMode || (dealerMode && !editInvoice && docKind === "order");
  // Editable contact + shipping address — pre-filled from the customer's
  // Airtable row.  When the admin clicks Save we persist any edits back to
  // the Users record alongside creating the Order.
  const [phone, setPhone] = useState(customer?.phone || customer?.phone_no || "");
  const [addr, setAddr] = useState(customer?.address || "");
  const [addr2, setAddr2] = useState(customer?.address2 || "");
  const [city, setCity] = useState(customer?.city || "");
  const [stateAbbr, setStateAbbr] = useState(customer?.state || "");
  const [zipc, setZipc] = useState(customer?.postcode || customer?.zip || "");
  const [country, setCountry] = useState(customer?.country || "US");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // The customer's pricing tier (role) so the product picker can load their
  // role price into the "Your price" box, with MSRP shown for reference.
  const [tier, setTier] = useState(customer?.role_name || customer?.role || "");

  // When the customer changes, fetch the FULL profile from Airtable so the
  // shipping form is always populated with the latest record — regardless
  // of which fields the CRM directory cache happens to expose.  This is
  // one extra GET per "+ Order" click, but the profile endpoint is cheap
  // and the cache problem is too painful to debug field-by-field.
  /* eslint-disable */
  useEffect(() => {
    if (!open || !customer?.id) return;
    let active = true;
    // Optimistic pre-fill from whatever the directory had
    setPhone(customer.phone || customer.phone_no || "");
    setAddr(customer.address || "");
    setAddr2(customer.address2 || "");
    setCity(customer.city || "");
    setStateAbbr(customer.state || "");
    setZipc(customer.postcode || customer.zip || "");
    setCountry(customer.country || "US");
    (async () => {
      try {
        const res = await getProfile(customer.id);
        const p = res?.profile;
        if (!active || !p) return;
        if (p.phone_no) setPhone(p.phone_no);
        if (p.address) setAddr(p.address);
        if (p.address2) setAddr2(p.address2);
        if (p.city) setCity(p.city);
        if (p.state) setStateAbbr(p.state);
        // Profile endpoint returns `postcode` / `zip` / `country` if defined
        if (p.postcode || p.zip) setZipc(String(p.postcode || p.zip || ""));
        if (p.country) setCountry(p.country);
        if (p.role_name) setTier(p.role_name);
      } catch {
        /* directory pre-fill stays */
      }
    })();
    return () => {
      active = false;
    };
  }, [open, customer?.id]);

  // Editing an existing invoice: prefill its line items and amounts.
  /* eslint-disable */
  useEffect(() => {
    if (!open || !editInvoice) return;
    setItems(
      (editInvoice.items || []).map((it) => ({
        product_record_id: it.product_record_id || "",
        product_name: it.name || it.product_name || "Item",
        image_url: it.image_url || "",
        qty: Number(it.qty || 1),
        price: Number(it.unit_price ?? it.price ?? 0),
        msrp: Number(it.msrp || 0),
      }))
    );
    setShipping(editInvoice.shipping_charge ? String(editInvoice.shipping_charge) : "");
    // Back-compute the tax % from the stored tax $ and taxable base so the
    // rate field repopulates correctly when editing.
    const editSubtotal = (editInvoice.items || []).reduce(
      (s, it) => s + Number(it.qty || 1) * Number(it.unit_price ?? it.price ?? 0),
      0
    );
    const editDiscount = Number(editInvoice.discount_amount || 0);
    const editTax = Number(editInvoice.tax || 0);
    const taxable = editSubtotal - editDiscount;
    setTaxRate(editTax > 0 && taxable > 0 ? String(Math.round((editTax / taxable) * 10000) / 100) : "");
    setDiscount(editInvoice.discount_amount ? String(editInvoice.discount_amount) : "");
    setPaymentMethod(editInvoice.payment_method || "");
  }, [open, editInvoice?.id]);
  /* eslint-enable */
  /* eslint-enable */

  const subtotal = useMemo(
    () =>
      items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0),
    [items]
  );
  // Savings vs retail (MSRP) from the customer's pricing tier / price edits.
  const tierSavings = useMemo(
    () =>
      items.reduce((s, it) => {
        const m = Number(it.msrp) || 0;
        const p = Number(it.price) || 0;
        return m > p ? s + (m - p) * (Number(it.qty) || 0) : s;
      }, 0),
    [items]
  );
  const totalSaved = tierSavings + (Number(discount) || 0);
  // Tax is a manual % entered by the dealer; the system computes the $ amount
  // on the taxable base (subtotal − discount).
  const taxAmount = useMemo(() => {
    const taxable = Math.max(0, subtotal - Number(discount || 0));
    return Math.round(taxable * (Number(taxRate || 0) / 100) * 100) / 100;
  }, [subtotal, discount, taxRate]);
  const total = useMemo(
    () =>
      Math.round(
        (subtotal +
          Number(shipping || 0) +
          taxAmount -
          Number(discount || 0)) *
          100
      ) / 100,
    [subtotal, shipping, taxAmount, discount]
  );

  function addItem(picked) {
    setItems((cur) => [
      ...cur,
      {
        product_record_id: picked.product_record_id,
        product_name: picked.name,
        image_url: picked.image_url || "",
        qty: picked.qty,
        price: picked.price,
        msrp: Number(picked.msrp) || 0,
      },
    ]);
    setPickerOpen(false);
  }

  function removeItem(idx) {
    setItems((cur) => cur.filter((_, i) => i !== idx));
  }

  function updateQty(idx, qty) {
    setItems((cur) =>
      cur.map((it, i) => (i === idx ? { ...it, qty: Number(qty) || 0 } : it))
    );
  }

  function updatePrice(idx, price) {
    setItems((cur) =>
      cur.map((it, i) =>
        i === idx ? { ...it, price: Number(price) || 0 } : it
      )
    );
  }

  function reset() {
    setItems([]);
    setShipping("");
    setTaxRate("");
    setDiscount("");
    setDocKind("invoice");
  }

  async function save() {
    if (items.length === 0) {
      toast.error("Add at least one product");
      return;
    }
    setSaving(true);
    try {
      if (editInvoice) {
        const res = await updateInvoice({
          caller_user_record_id: callerUserRecordId,
          user_record_id: callerUserRecordId,
          invoice_record_id: editInvoice.id,
          items: items.map((it) => ({
            product_record_id: it.product_record_id,
            name: it.product_name,
            image_url: it.image_url,
            qty: it.qty,
            price: it.price,
          })),
          shipping_charge: Number(shipping || 0),
          tax: taxAmount,
          discount: Number(discount || 0),
          payment_method: paymentMethod,
        });
        toast.success(`Invoice updated — ${fmt(res.total)}`);
        onCreated?.(res);
        reset();
        onOpenChange(false);
        return;
      }
      if (dealerMode && !asOrder) {
        const res = await createInvoice({
          caller_user_record_id: callerUserRecordId,
          user_record_id: customer.id,
          items: items.map((it) => ({
            product_record_id: it.product_record_id,
            name: it.product_name,
            image_url: it.image_url,
            qty: it.qty,
            price: it.price,
          })),
          shipping_charge: Number(shipping || 0),
          tax: taxAmount,
          discount: Number(discount || 0),
          shipping_phone: phone,
          shipping_address: addr,
          shipping_city: city,
          shipping_state: stateAbbr,
          shipping_zip: zipc,
          status: "pending",
          payment_method: paymentMethod,
        });
        toast.success(`Invoice #${res.invoice_number} created — ${fmt(res.total)}`, {
          description: "Saved to your invoice ledger. Open it to email the customer.",
        });
        onCreated?.(res);
        reset();
        onOpenChange(false);
        return;
      }
      const res = await adminCreateOrder({
        callerUserRecordId,
        userRecordId: customer.id,
        items: items.map((it) => ({
          product_record_id: it.product_record_id,
          qty: it.qty,
          price: it.price,
        })),
        shippingCharge: Number(shipping || 0),
        tax: taxAmount,
        discount: Number(discount || 0),
        shippingPhone: phone,
        shippingAddress: addr,
        shippingAddress2: addr2,
        shippingCity: city,
        shippingState: stateAbbr,
        shippingZip: zipc,
        shippingCountry: country,
        updateBuyerRecord: true,
        status: "Pending",
      });
      if (res.dealer_order) {
        toast.success(`Order sent for review · ${fmt(res.total)}`, {
          description: "Your team will confirm and process it shortly.",
        });
      } else {
        toast.success(`Order #${res.order_number} created — ${fmt(res.total)}`);
      }
      for (const w of res.inventory_warnings || []) {
        toast.warning(w.message, { duration: 9000, description: "Fulfilment inventory — order was still created." });
      }
      onCreated?.(res);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Could not save"
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(o) => {
          if (!o) reset();
          onOpenChange(o);
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto p-0 bg-white"
          data-testid="draft-order-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-zinc-200">
            <SheetTitle className="flex items-center gap-2 text-lg">
              <ShoppingBag size={18} className="text-emerald-600" />
              {editInvoice ? "Edit invoice" : asOrder ? "New order" : "New invoice"}
            </SheetTitle>
            <SheetDescription className="text-xs text-zinc-500">
              {customer?.full_name || customer?.email || "Customer"} ·{" "}
              {asOrder && dealerMode
                ? "sent to the OreVital team for review and fulfilment."
                : dealerMode
                  ? "saved to your invoice ledger — email or print it from Orders."
                  : "nothing is saved to Airtable until you click Save."}
            </SheetDescription>
            {dealerMode && !editInvoice && (
              <div className="mt-2 flex rounded-full border border-zinc-200 bg-zinc-50 p-0.5 text-xs font-semibold" data-testid="draft-kind-toggle">
                {[["invoice", "Invoice"], ["order", "Add order"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setDocKind(k)} className={`flex-1 h-8 rounded-full transition-colors ${docKind === k ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid={`draft-kind-${k}`}>{l}</button>
                ))}
              </div>
            )}
          </SheetHeader>

          <div className="px-5 py-4 space-y-4">
            {/* Line items */}
            {items.length === 0 ? (
              <div className="py-8 text-center text-zinc-500 text-sm">
                No items yet. Click <strong>Add product</strong> to start.
              </div>
            ) : (
              <ul className="rounded-2xl border border-zinc-200 divide-y divide-zinc-200">
                {items.map((it, idx) => (
                  <li
                    key={it.product_record_id || `slot-${idx}`}
                    className="p-3 flex items-center gap-2.5"
                    data-testid={`draft-item-${idx}`}
                  >
                    {it.image_url ? (
                      <img
                        src={it.image_url}
                        alt={it.product_name}
                        className="w-11 h-11 rounded-lg object-cover bg-zinc-100 shrink-0"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0">
                        <Package size={16} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 truncate">
                        {it.product_name}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Input
                          type="number"
                          value={it.qty}
                          onChange={(e) => updateQty(idx, e.target.value)}
                          className="h-8 w-16 text-xs"
                          data-testid={`draft-item-qty-${idx}`}
                          min="1"
                        />
                        <span className="text-xs text-zinc-400">×</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={it.price}
                          onChange={(e) => updatePrice(idx, e.target.value)}
                          className="h-8 w-20 text-xs"
                          data-testid={`draft-item-price-${idx}`}
                        />
                        <span className="text-xs font-semibold text-zinc-700 ml-2">
                          ={" "}
                          {fmt(
                            (Number(it.qty) || 0) * (Number(it.price) || 0)
                          )}
                        </span>
                      </div>
                      {Number(it.msrp) > Number(it.price) && (
                        <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`draft-item-save-${idx}`}>
                          <span className="line-through text-zinc-400 mr-1">{fmt(it.msrp)}</span>
                          save {fmt((Number(it.msrp) - Number(it.price)) * (Number(it.qty) || 0))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(idx)}
                      className="text-rose-500 hover:text-rose-600 shrink-0"
                      data-testid={`draft-item-rm-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <Button
              variant="outline"
              onClick={() => setPickerOpen(true)}
              className="w-full rounded-xl border-dashed border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="draft-add-product-btn"
            >
              <Plus size={14} className="mr-1" />
              Add product
            </Button>

            {/* Shipping address — pre-filled from the customer, editable */}
            <div className="rounded-2xl border border-zinc-200 p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Contact & shipping
              </div>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                className="h-9 text-sm"
                data-testid="draft-phone"
              />
              <Input
                value={addr}
                onChange={(e) => setAddr(e.target.value)}
                placeholder="Street address"
                className="h-9 text-sm"
                data-testid="draft-addr1"
              />
              <Input
                value={addr2}
                onChange={(e) => setAddr2(e.target.value)}
                placeholder="Apt / Suite (optional)"
                className="h-9 text-sm"
                data-testid="draft-addr2"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="h-9 text-sm"
                  data-testid="draft-city"
                />
                <Input
                  value={stateAbbr}
                  onChange={(e) => setStateAbbr(e.target.value)}
                  placeholder="State"
                  className="h-9 text-sm"
                  data-testid="draft-state"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={zipc}
                  onChange={(e) => setZipc(e.target.value)}
                  placeholder="ZIP"
                  className="h-9 text-sm"
                  data-testid="draft-zip"
                />
                <select
                  value={country || "US"}
                  onChange={(e) => setCountry(e.target.value)}
                  className="h-9 rounded-md border border-zinc-200 px-2 text-sm bg-white"
                  data-testid="draft-country"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </select>
              </div>
              <div className="text-[10px] text-zinc-400 pt-0.5">
                Edits here will also update {customer?.first_name || "the customer"}&apos;s contact record on save.
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-2xl bg-zinc-50 p-4 space-y-2 text-sm">
              {tierSavings > 0 && (
                <>
                  <Row label="Retail value (MSRP)" value={fmt(subtotal + tierSavings)} />
                  <Row label="Price savings" value={`−${fmt(tierSavings)}`} testId="draft-tier-savings" />
                </>
              )}
              <Row label="Subtotal" value={fmt(subtotal)} />
              <div className="flex justify-between items-center">
                <span className="text-zinc-500">Shipping</span>
                <Input
                  type="number"
                  step="0.01"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  className="h-8 w-24 text-right text-xs"
                  data-testid="draft-shipping"
                  placeholder="0"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-500">
                  Tax (%)
                  {taxAmount > 0 && (
                    <span className="ml-1 text-zinc-400">= {fmt(taxAmount)}</span>
                  )}
                </span>
                <div className="relative w-24">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="h-8 w-24 text-right text-xs pr-5"
                    data-testid="draft-tax-rate"
                    placeholder="0"
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400">%</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-emerald-700">Discount (−$)</span>
                <Input
                  type="number"
                  step="0.01"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="h-8 w-24 text-right text-xs"
                  data-testid="draft-discount"
                  placeholder="0"
                />
              </div>
              {dealerMode && !asOrder && (
                <div className="flex items-center justify-between text-xs py-1">
                  <span className="text-zinc-500">Payment method</span>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs"
                    data-testid="draft-payment-method"
                  >
                    <option value="">Not set</option>
                    {["Cash", "Credit/Debit Card", "Check", "ACH/Bank Transfer", "Other"].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="border-t border-zinc-200 my-1" />
              <Row label="Total" value={fmt(total)} strong testId="draft-total" />
              {totalSaved > 0 && (
                <div className="flex justify-end">
                  <span className="inline-block rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold px-3 py-1" data-testid="draft-total-saved">
                    Customer saves {fmt(totalSaved)}
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
                className="flex-1 rounded-xl"
                data-testid="draft-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving || items.length === 0}
                className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="draft-save-btn"
              >
                {saving ? (
                  <Loader2 size={13} className="animate-spin mr-1" />
                ) : (
                  <Save size={13} className="mr-1" />
                )}
                {editInvoice ? "Save changes" : asOrder ? (dealerMode ? "Send order" : "Save order") : "Save invoice"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AddProductDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        userRecordId={callerUserRecordId}
        orderRecordId={null}
        tier={tier}
        skipApiCall={true}
        onAdded={addItem}
      />
    </>
  );
}

function Row({ label, value, strong, testId }) {
  return (
    <div className="flex justify-between">
      <span className={strong ? "font-semibold text-zinc-900" : "text-zinc-500"}>
        {label}
      </span>
      <span
        className={
          strong ? "font-bold text-emerald-700 text-base" : "text-zinc-700"
        }
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}
